import { type ChangeEvent, useState } from 'react';
import './Conents.styles.css';
import dayjs from 'dayjs';
import searchIcon from '../../../../assets/img/search.png';
import { ArticleSearchCard } from '../../../../components/articlcle-search-card/ArticleSearchCard.tsx';
import { useSearchParams } from 'react-router-dom';
import { Divider, type GetProps, Input, Select } from 'antd';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import type { RegulationCardType } from '../../Dashboard.types.ts';
import { useService } from '../../Dashboard.hooks.ts';

type SearchProps = GetProps<typeof Input.Search>;

export default function ContentTopic() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('searchTerm') || '');
  const [sort, setSort] = useState<string | null>(t('common.not-selected'));
  const type = searchParams.getAll('types') || ([] as string[]);
  const search = searchParams.get('searchTerm');
  const unit = searchParams.getAll('unitInChargeIds');
  const topic = searchParams.get('topicId');
  const { searchedRegulations } = useService({ type, unit, search, topic });

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const onSearch: SearchProps['onSearch'] = (value) => {
    if (value) {
      searchParams.set('searchTerm', value);
    } else {
      searchParams.delete('searchTerm');
    }
    setSearchParams(searchParams);
  };

  const handleSort = (value: string) => {
    setSort(value);
  };

  const handleResetSort = () => {
    setSort(t('common.not-selected'));
  };

  const sortData = (data: RegulationCardType[]) => {
    switch (sort) {
      case 'date-desc': {
        return data?.sort((a, b) => dayjs(a.creationDate).unix() - dayjs(b.creationDate).unix());
      }
      case 'date-asc': {
        return data?.sort((a, b) => dayjs(b.creationDate).unix() - dayjs(a.creationDate).unix());
      }
      case 'title-asc': {
        return data?.sort((a, b) => a.title.localeCompare(b.title));
      }
      case 'title-desc': {
        return data?.sort((a, b) => b.title.localeCompare(a.title));
      }
      case t('common.not-selected'): {
        return data?.sort((a, b) => b.title.localeCompare(a.title));
      }
      default: {
        return data;
      }
    }
  };

  return (
    <div className="search-container">
      <h1>{t('common.search-results')}</h1>
      <div className="search-section">
        <Input.Search
          placeholder={t('form.placeholder.type-here')}
          variant="filled"
          size="large"
          value={searchTerm}
          onChange={handleSearch}
          onSearch={onSearch}
        />
      </div>
      <div className="search-section">
        <Select
          placeholder={t('common.not-selected')}
          style={{ width: 200 }}
          onChange={handleSort}
          value={sort}
          size="large"
          popupRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '8px 0' }} />
              <div className="select-action" onClick={handleResetSort}>
                {t('common.reset-sorting')}
              </div>
            </>
          )}
          options={[
            {
              value: 'date-asc',
              label: (
                <div className="select-option">
                  <FiArrowDown />
                  <span>{t('common.date-asc')}</span>
                </div>
              ),
            },
            {
              value: 'date-desc',
              label: (
                <div className="select-option">
                  <FiArrowUp />
                  <span>{t('common.date-desc')}</span>
                </div>
              ),
            },
            {
              value: 'title-asc',
              label: (
                <div className="select-option">
                  <FiArrowDown />
                  <span>{t('common.name-asc')}</span>
                </div>
              ),
            },
            {
              value: 'title-desc',
              label: (
                <div className="select-option">
                  <FiArrowUp />
                  <span>{t('common.name-desc')}</span>
                </div>
              ),
            },
          ]}
        />
      </div>
      {searchedRegulations?.length ? (
        sortData(searchedRegulations)?.map((regulation: RegulationCardType) => (
          <ArticleSearchCard
            id={regulation.id}
            title={regulation.title}
            type={regulation.type}
            confirmed={Boolean(regulation.confirmationDate)}
            created={dayjs(regulation?.creationDate).format('DD.MM.YYYY') || 'Not set'}
            status={dayjs(regulation?.confirmationDate).format('DD.MM.YYYY') || 'Not set'}
            shortDescription={regulation.shortDescription}
            key={regulation.id}
          />
        ))
      ) : (
        <div className="no-results-container">
          <div className="no-results-imager">
            <img src={searchIcon} alt="no results" />{' '}
          </div>
          <div className="no-results-alert">{t('common.not-found')}</div>
          <div className="no-results-description">{t('common.not-found-hint')}</div>
        </div>
      )}
    </div>
  );
}
